import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Microscope, MessageSquare, Sparkles, AlertCircle, CreditCard, RefreshCw, Download, FileSpreadsheet, FileJson, Search, Filter, MoreHorizontal, ThumbsUp, MessageCircle, TrendingUp, SmilePlus, Calendar, User, ChevronDown, ChevronUp, Clock, CheckCircle2, BarChart3, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle, Circle, Brain, Tags, Users, Link2, Activity, Lock } from 'lucide-react';
import { useEnhancedComments } from '../hooks/useEnhancedComments';
import { CommentsProgress } from '../components/research/CommentsProgress';
import { CreditWarningModal } from '../components/research/CreditWarningModal';
import { PhoneLinkModal } from '../components/research/PhoneLinkModal';
import { getCreditBalance, exportCommentsCSV, exportCommentsJSON, checkLinkedPhone } from '../lib/api';
// Types for local state
type SortField = 'likes' | 'replies' | 'date' | 'sentiment';
type SortOrder = 'asc' | 'desc';
type SentimentFilter = 'all' | 'positive' | 'negative' | 'neutral';
export function ResearchAnalysis() {
  const {
    transcriptionId
  } = useParams<{
    transcriptionId: string;
  }>();
  const navigate = useNavigate();
  // Global State
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState<string | null>(null);
  // Phone linking state
  const [showPhoneLinkModal, setShowPhoneLinkModal] = useState(false);
  const [linkedPhone, setLinkedPhone] = useState<string | null>(null);
  const [phoneChecked, setPhoneChecked] = useState(false);
  // Local UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('likes');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>('all');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const {
    preview,
    previewLoading,
    previewError,
    fetching,
    progress,
    results,
    showCreditModal,
    setShowCreditModal,
    alreadyFetched,
    insufficientCredits,
    creditsError,
    initiateFetch,
    loadResults
  } = useEnhancedComments(transcriptionId || '');
  // State for phone link modal
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  // Load credits on mount or error
  useEffect(() => {
    loadCredits();
    loadVideoTitle();
    checkPhone();
  }, [insufficientCredits]);
  const loadCredits = async () => {
    try {
      const balance = await getCreditBalance();
      setCurrentCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };
  const loadVideoTitle = async () => {
    if (!transcriptionId) return;
    try {
      const {
        data
      } = await import('../lib/supabase').then(m => m.supabase.from('transcriptions').select('title').eq('task_id', transcriptionId).single());
      if (data?.title) setVideoTitle(data.title);
    } catch {
      // silently fail — UUID fallback is fine
    }
  };
  const checkPhone = async () => {
    try {
      const result = await checkLinkedPhone();
      if (result.linked && result.phone_number) {
        setLinkedPhone(result.phone_number);
        setCurrentCredits(result.credits_remaining);
      }
      setPhoneChecked(true);
    } catch (error) {
      console.error('Error checking linked phone:', error);
      setPhoneChecked(true);
    }
  };
  // Handlers
  const handleFetchClick = () => {
    // If phone is linked and has enough credits, go straight to credit confirm modal
    if (linkedPhone && currentCredits !== null && currentCredits >= (preview?.credits_needed || 0)) {
      setShowCreditModal(true);
    } else {
      // Otherwise show phone-link modal (handles both unlinked + insufficient credits)
      setShowPhoneLinkModal(true);
    }
  };
  const handleConfirmFetch = () => initiateFetch();
  const handlePhoneLinkConfirm = (phoneNumber: string, sessionToken: string) => {
    setLinkedPhone(phoneNumber);
    setShowPhoneLinkModal(false);
    // Proceed with fetch — phone is now linked and credits confirmed
    initiateFetch();
  };
  const handleExportCSV = async () => {
    if (!transcriptionId) return;
    setIsExporting(true);
    try {
      await exportCommentsCSV(transcriptionId);
    } catch (err) {
      setExportError('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };
  const handleExportJSON = async () => {
    if (!transcriptionId) return;
    setIsExporting(true);
    try {
      await exportCommentsJSON(transcriptionId);
    } catch (err) {
      setExportError('Failed to export JSON');
    } finally {
      setIsExporting(false);
    }
  };
  const toggleCommentExpand = (index: number) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedComments(newExpanded);
  };
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  // Helper: resolve display name from any author field
  const getAuthorName = (comment: any): string => {
    const displayName = comment.author_name || comment.author || '';
    const username = comment.author_username || '';
    // If both exist and are different, prefer display name
    // If they're the same value, just return one
    return displayName || username || 'Anonymous';
  };
  // Helper: resolve avatar URL
  const getAvatarUrl = (comment: any): string | null => comment.author_avatar_url || comment.author_avatar || null;
  // Helper: resolve reply count
  const getReplyCount = (comment: any): number => comment.reply_count ?? comment.replies ?? 0;
  // Helper: resolve like count
  const getLikeCount = (comment: any): number => comment.digg_count ?? comment.likes ?? 0;
  // Helper: resolve timestamp display
  const getTimestamp = (comment: any): string => {
    if (comment.created_at_timestamp) {
      // TikTok returns Unix epoch in seconds — multiply by 1000 for JS Date
      const ms = comment.created_at_timestamp > 1e10 ? comment.created_at_timestamp // already milliseconds
      : comment.created_at_timestamp * 1000; // seconds → milliseconds
      const d = new Date(ms);
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
    }
    if (comment.created_at) {
      const d = new Date(comment.created_at);
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
    }
    if (comment.timestamp) {
      const d = new Date(comment.timestamp);
      return isNaN(d.getTime()) ? comment.timestamp : d.toLocaleDateString();
    }
    return '—';
  };
  // Derived Data (Filtering & Sorting)
  const processedComments = useMemo(() => {
    if (!results?.top_comments) return [];
    let filtered = results.top_comments.filter(comment => {
      const name = getAuthorName(comment);
      const matchesSearch = !searchTerm || name.toLowerCase().includes(searchTerm.toLowerCase()) || (comment.comment || comment.text || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSentiment = sentimentFilter === 'all' || (comment.sentiment || 'neutral').toLowerCase() === sentimentFilter;
      return matchesSearch && matchesSentiment;
    });
    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'likes':
          comparison = getLikeCount(a) - getLikeCount(b);
          break;
        case 'replies':
          comparison = getReplyCount(a) - getReplyCount(b);
          break;
        case 'date':
          const dateA = a.created_at_timestamp ? a.created_at_timestamp * 1000 : a.created_at ? new Date(a.created_at).getTime() : a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const dateB = b.created_at_timestamp ? b.created_at_timestamp * 1000 : b.created_at ? new Date(b.created_at).getTime() : b.timestamp ? new Date(b.timestamp).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'sentiment':
          comparison = (a.sentiment || '').localeCompare(b.sentiment || '');
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [results, searchTerm, sortField, sortOrder, sentimentFilter]);
  // Pagination
  const totalPages = Math.ceil(processedComments.length / itemsPerPage);
  const paginatedComments = processedComments.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Render Helpers
  const renderSentimentBadge = (sentiment?: string) => {
    const s = (sentiment || 'neutral').toLowerCase();
    let colorClass = 'bg-secondary/50 text-muted-foreground';
    if (s === 'positive') colorClass = 'bg-success/10 text-success';
    if (s === 'negative') colorClass = 'bg-destructive/10 text-destructive';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colorClass}`} data-id="element-2218">
        {s}
      </span>;
  };
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 opacity-30" data-id="element-2219" />;
    return sortOrder === 'asc' ? <ArrowUp size={14} className="ml-1" data-id="element-2220" /> : <ArrowDown size={14} className="ml-1" data-id="element-2221" />;
  };
  // Research roadmap data
  const researchCapabilities = [{
    category: 'Comment Extraction',
    status: 'live' as const,
    icon: MessageSquare,
    features: [{
      name: 'Full comment dataset extraction',
      live: true
    }, {
      name: 'Real-time fetch progress tracking',
      live: true
    }, {
      name: 'Paginated data with sort (likes/recent/replies)',
      live: true
    }, {
      name: 'CSV & JSON export',
      live: true
    }]
  }, {
    category: 'Basic Analytics',
    status: 'live' as const,
    icon: BarChart3,
    features: [{
      name: 'Aggregate stats (total, avg likes, engagement rate)',
      live: true
    }, {
      name: 'Keyword-based sentiment classification',
      live: true
    }, {
      name: 'Top comments by engagement',
      live: true
    }, {
      name: 'Search & filter across dataset',
      live: true
    }]
  }, {
    category: 'AI-Powered Sentiment',
    status: 'coming' as const,
    icon: Brain,
    features: [{
      name: 'GPT/Claude sentiment scoring per comment',
      live: false
    }, {
      name: 'Emotion detection (anger, joy, fear, surprise)',
      live: false
    }, {
      name: 'Sarcasm & irony detection',
      live: false
    }, {
      name: 'Confidence scores per classification',
      live: false
    }]
  }, {
    category: 'Topic Clustering',
    status: 'coming' as const,
    icon: Tags,
    features: [{
      name: 'Automatic theme discovery across comments',
      live: false
    }, {
      name: 'Keyword frequency & co-occurrence maps',
      live: false
    }, {
      name: 'Named entity recognition (people, places, brands)',
      live: false
    }, {
      name: 'Topic evolution over comment timeline',
      live: false
    }]
  }, {
    category: 'Audience Signals',
    status: 'coming' as const,
    icon: Users,
    features: [{
      name: 'Commenter demographic inference',
      live: false
    }, {
      name: 'Repeat commenter identification',
      live: false
    }, {
      name: 'Influencer vs. casual commenter segmentation',
      live: false
    }, {
      name: 'Geographic signal extraction',
      live: false
    }]
  }, {
    category: 'Transcript Correlation',
    status: 'coming' as const,
    icon: Link2,
    features: [{
      name: 'Map comments to transcript timestamps',
      live: false
    }, {
      name: 'Identify which moments drive most engagement',
      live: false
    }, {
      name: 'Quote extraction from comments',
      live: false
    }, {
      name: 'Content-audience resonance scoring',
      live: false
    }]
  }];
  return <div className="min-h-screen bg-background text-foreground pb-20" data-id="element-2222">
      {/* 1. HEADER BAR */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10" data-id="element-2223">
        <div className="container mx-auto px-4 py-4 max-w-7xl" data-id="element-2224">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2225">
            <div className="flex items-center gap-4" data-id="element-2226">
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full" onClick={() => navigate(-1)} data-id="element-2227">
                <ArrowLeft size={18} data-id="element-2228" />
              </Button>
              <div data-id="element-2229">
                <div className="flex items-center gap-2" data-id="element-2230">
                  <h1 className="text-lg font-semibold flex items-center gap-2" data-id="element-2231">
                    <Microscope size={18} className="text-primary" data-id="element-2232" />
                    Comment Research Workbench
                  </h1>
                  <span className="bg-destructive/10 text-destructive border border-destructive/20 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-id="element-2233">
                    Advanced
                  </span>
                </div>
                <p className="text-xs text-muted-foreground hidden md:block" data-id="element-2234">
                  {videoTitle || transcriptionId}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-2235">
              {currentCredits !== null && <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/50 rounded-full border border-border/50" data-id="element-2236">
                  <CreditCard size={14} className="text-muted-foreground" data-id="element-2237" />
                  <span className="text-sm font-medium" data-id="element-2238">
                    {currentCredits} credits
                  </span>
                </div>}

              {(alreadyFetched || results) && <div className="flex items-center gap-2 border-l border-border pl-3 ml-1" data-id="element-2239">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportCSV} disabled={isExporting} data-id="element-2240">
                    <FileSpreadsheet size={14} className="mr-2" data-id="element-2241" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportJSON} disabled={isExporting} data-id="element-2242">
                    <FileJson size={14} className="mr-2" data-id="element-2243" />
                    JSON
                  </Button>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl space-y-8" data-id="element-2244">
        {/* 2. STATE: PREVIEW (Ready to Fetch) */}
        {!fetching && !alreadyFetched && !results && !insufficientCredits && <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2245">
            {/* Left: Preview List */}
            <div className="lg:col-span-2 space-y-4" data-id="element-2246">
              <div className="flex items-center justify-between" data-id="element-2247">
                <h2 className="text-lg font-semibold flex items-center gap-2" data-id="element-2248">
                  <MessageSquare size={18} data-id="element-2249" />
                  Preview Comments
                </h2>
                <span className="text-sm text-muted-foreground" data-id="element-2250">
                  Showing sample of{' '}
                  {preview?.estimated_total.toLocaleString() || 0} available
                </span>
              </div>

              {previewLoading ? <div className="space-y-2" data-id="element-2251">
                  {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-secondary/20 rounded animate-pulse" data-id="element-2252" />)}
                </div> : <div className="rounded-md border border-border overflow-hidden" data-id="element-2253">
                  {/* Spreadsheet-style table */}
                  <div className="overflow-x-auto" data-id="element-2254">
                    <table className="w-full text-sm text-left border-collapse" data-id="element-2255">
                      <thead data-id="element-2256">
                        <tr className="bg-secondary/40 border-b-2 border-border" data-id="element-2257">
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-10 text-center border-r border-border" data-id="element-2258">
                            #
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-36 border-r border-border" data-id="element-2259">
                            Author
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground border-r border-border" data-id="element-2260">
                            Comment
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-20 text-right border-r border-border" data-id="element-2261">
                            Likes
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-20 text-right border-r border-border" data-id="element-2262">
                            Replies
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-28 text-right" data-id="element-2263">
                            Timestamp
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2264">
                        {preview?.preview_comments.map((comment, i) => {
                    const name = getAuthorName(comment);
                    const avatarUrl = getAvatarUrl(comment);
                    return <tr key={i} className={`border-b border-border/60 hover:bg-primary/5 transition-colors ${i % 2 === 0 ? 'bg-background' : 'bg-secondary/10'}`} data-id="element-2265">
                              <td className="px-3 py-2 text-xs text-muted-foreground text-center border-r border-border/40 font-mono" data-id="element-2266">
                                {i + 1}
                              </td>
                              <td className="px-3 py-2 border-r border-border/40" data-id="element-2267">
                                <div className="flex items-center gap-2" data-id="element-2268">
                                  {avatarUrl ? <img src={avatarUrl} alt={name} className="w-5 h-5 rounded-sm object-cover flex-shrink-0" data-id="element-2269" /> : <div className="w-5 h-5 rounded-sm bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary flex-shrink-0" data-id="element-2270">
                                      {name.substring(0, 1).toUpperCase()}
                                    </div>}
                                  <span className="text-xs font-medium truncate max-w-[100px]" title={name} data-id="element-2271">
                                    {name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2 border-r border-border/40" data-id="element-2272">
                                <span className="text-xs text-foreground/90 leading-relaxed line-clamp-2" data-id="element-2273">
                                  {comment.comment || comment.text || '—'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right border-r border-border/40" data-id="element-2274">
                                <span className="text-xs font-mono text-foreground" data-id="element-2275">
                                  {getLikeCount(comment).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right border-r border-border/40" data-id="element-2276">
                                <span className="text-xs font-mono text-muted-foreground" data-id="element-2277">
                                  {getReplyCount(comment).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right" data-id="element-2278">
                                <span className="text-xs text-muted-foreground whitespace-nowrap" data-id="element-2279">
                                  {getTimestamp(comment)}
                                </span>
                              </td>
                            </tr>;
                  })}
                        {/* "More rows" indicator row */}
                        {(preview?.estimated_total || 0) > (preview?.preview_comments?.length || 0) && <tr className="bg-secondary/5 border-t border-dashed border-border" data-id="element-2280">
                            <td colSpan={6} className="px-3 py-3 text-center text-xs text-muted-foreground" data-id="element-2281">
                              <span className="inline-flex items-center gap-1.5" data-id="element-2282">
                                <span className="text-base leading-none" data-id="element-2283">
                                  ⋯
                                </span>
                                {((preview?.estimated_total || 0) - (preview?.preview_comments?.length || 0)).toLocaleString()}{' '}
                                more rows available — fetch to unlock full
                                dataset
                              </span>
                            </td>
                          </tr>}
                      </tbody>
                    </table>
                  </div>
                  {/* Row count footer */}
                  <div className="px-3 py-2 bg-secondary/20 border-t border-border flex items-center justify-between text-xs text-muted-foreground" data-id="element-2284">
                    <span data-id="element-2285">
                      Showing {preview?.preview_comments?.length || 0} preview
                      rows
                    </span>
                    <span className="font-medium" data-id="element-2286">
                      {(preview?.estimated_total || 0).toLocaleString()} total
                      rows estimated
                    </span>
                  </div>
                </div>}
            </div>

            {/* Right: Sticky Action Panel */}
            <div className="lg:col-span-1" data-id="element-2287">
              <div className="sticky top-24" data-id="element-2288">
                <Card className="p-6 border-primary/20 shadow-lg shadow-primary/5 bg-card relative overflow-hidden" data-id="element-2289">
                  <div className="absolute top-0 right-0 p-3 opacity-10" data-id="element-2290">
                    <Sparkles size={120} data-id="element-2291" />
                  </div>

                  <h3 className="text-xl font-bold mb-6 relative z-10" data-id="element-2292">
                    Ready to Extract
                  </h3>

                  <div className="space-y-6 relative z-10" data-id="element-2293">
                    <div className="grid grid-cols-2 gap-4" data-id="element-2294">
                      <div className="p-3 bg-secondary/30 rounded-lg" data-id="element-2295">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1" data-id="element-2296">
                          Total Comments
                        </div>
                        <div className="text-2xl font-bold" data-id="element-2297">
                          {preview?.estimated_total.toLocaleString() || 0}
                        </div>
                      </div>
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/20" data-id="element-2298">
                        <div className="text-xs text-primary uppercase tracking-wider mb-1" data-id="element-2299">
                          Credits Needed
                        </div>
                        <div className="text-2xl font-bold text-primary" data-id="element-2300">
                          {preview?.credits_needed || 0}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-muted-foreground bg-secondary/20 p-3 rounded-md flex gap-2 items-start" data-id="element-2301">
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" data-id="element-2302" />
                      <div data-id="element-2303">
                        <p className="font-medium text-foreground" data-id="element-2304">
                          One-time extraction cost
                        </p>
                        <p className="text-xs mt-1" data-id="element-2305">
                          1 credit per 100 comments. Once fetched, you can
                          analyze and export this data forever without extra
                          cost.
                        </p>
                      </div>
                    </div>

                    <Button size="lg" className="w-full h-14 text-lg shadow-xl shadow-primary/20" onClick={handleFetchClick} disabled={previewLoading || !preview || preview.estimated_total === 0} data-id="element-2306">
                      {previewLoading ? 'Analyzing...' : `Fetch All Comments`}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground" data-id="element-2307">
                      By clicking fetch, you agree to use this data for research
                      purposes only.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>}

        {/* RESEARCH ROADMAP — shown in preview state */}
        {!fetching && !alreadyFetched && !results && !insufficientCredits && <ResearchRoadmap capabilities={researchCapabilities} data-id="element-2308" />}

        {/* 3. STATE: FETCHING */}
        {fetching && progress && <div className="max-w-3xl mx-auto py-12" data-id="element-2309">
            <Card className="p-8 bg-gray-900 text-white border-none shadow-2xl relative overflow-hidden" data-id="element-2310">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-50" data-id="element-2311" />

              <div className="relative z-10 text-center mb-8" data-id="element-2312">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4 animate-pulse" data-id="element-2313">
                  <RefreshCw size={32} className="animate-spin text-primary-foreground" data-id="element-2314" />
                </div>
                <h2 className="text-2xl font-bold mb-2" data-id="element-2315">
                  Extracting Comment Data
                </h2>
                <p className="text-white/60" data-id="element-2316">
                  Please wait while we gather and analyze the comments...
                </p>
              </div>

              <div className="relative z-10 space-y-6" data-id="element-2317">
                {/* Progress Bar */}
                <div data-id="element-2318">
                  <div className="flex justify-between text-sm mb-2 font-medium" data-id="element-2319">
                    <span data-id="element-2320">Progress</span>
                    <span data-id="element-2321">{progress.progress_percent.toFixed(1)}%</span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden" data-id="element-2322">
                    <div className="h-full bg-primary transition-all duration-500 ease-out" style={{
                  width: `${progress.progress_percent}%`
                }} data-id="element-2323" />
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-id="element-2324">
                  <div className="bg-white/5 rounded-lg p-4 backdrop-blur-sm" data-id="element-2325">
                    <div className="text-xs text-white/50 uppercase tracking-wider mb-1" data-id="element-2326">
                      Fetched
                    </div>
                    <div className="text-xl font-bold" data-id="element-2327">
                      {progress.comments_fetched.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 backdrop-blur-sm" data-id="element-2328">
                    <div className="text-xs text-white/50 uppercase tracking-wider mb-1" data-id="element-2329">
                      Total
                    </div>
                    <div className="text-xl font-bold" data-id="element-2330">
                      {progress.estimated_total.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 backdrop-blur-sm" data-id="element-2331">
                    <div className="text-xs text-white/50 uppercase tracking-wider mb-1" data-id="element-2332">
                      Page
                    </div>
                    <div className="text-xl font-bold" data-id="element-2333">
                      {progress.current_page || 1}
                      <span className="text-sm text-white/40 font-normal" data-id="element-2334">
                        /{progress.total_pages_estimate || '?'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 backdrop-blur-sm" data-id="element-2335">
                    <div className="text-xs text-white/50 uppercase tracking-wider mb-1" data-id="element-2336">
                      ETA
                    </div>
                    <div className="text-xl font-bold" data-id="element-2337">
                      {progress.eta_seconds || 0}s
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>}

        {/* 4. STATE: INSUFFICIENT CREDITS */}
        {!fetching && insufficientCredits && creditsError && <div className="max-w-2xl mx-auto py-12" data-id="element-2338">
            <Card className="border-destructive/30 shadow-lg overflow-hidden" data-id="element-2339">
              <div className="bg-destructive/10 p-6 border-b border-destructive/20 flex items-center gap-4" data-id="element-2340">
                <div className="p-3 bg-destructive/20 rounded-full" data-id="element-2341">
                  <AlertCircle size={32} className="text-destructive" data-id="element-2342" />
                </div>
                <div data-id="element-2343">
                  <h2 className="text-xl font-bold text-destructive" data-id="element-2344">
                    Insufficient Credits
                  </h2>
                  <p className="text-destructive/80" data-id="element-2345">
                    You need more credits to perform this analysis.
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-6" data-id="element-2346">
                <div className="bg-secondary/20 rounded-lg border border-border overflow-hidden" data-id="element-2347">
                  <table className="w-full text-sm" data-id="element-2348">
                    <tbody className="divide-y divide-border/50" data-id="element-2349">
                      <tr className="bg-secondary/30" data-id="element-2350">
                        <td className="p-3 text-muted-foreground" data-id="element-2351">
                          Estimated Comments
                        </td>
                        <td className="p-3 font-medium text-right" data-id="element-2352">
                          {preview?.estimated_total.toLocaleString()}
                        </td>
                      </tr>
                      <tr data-id="element-2353">
                        <td className="p-3 text-muted-foreground" data-id="element-2354">
                          Credits Required
                        </td>
                        <td className="p-3 font-medium text-right" data-id="element-2355">
                          {preview?.credits_needed}
                        </td>
                      </tr>
                      <tr data-id="element-2356">
                        <td className="p-3 text-muted-foreground" data-id="element-2357">
                          Your Balance
                        </td>
                        <td className="p-3 font-medium text-right" data-id="element-2358">
                          {currentCredits}
                        </td>
                      </tr>
                      <tr className="bg-destructive/5" data-id="element-2359">
                        <td className="p-3 font-medium text-destructive" data-id="element-2360">
                          Shortfall
                        </td>
                        <td className="p-3 font-bold text-right text-destructive" data-id="element-2361">
                          {Math.max(0, (preview?.credits_needed || 0) - (currentCredits || 0))}{' '}
                          needed
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-4" data-id="element-2362">
                  <Button className="flex-1 h-12 text-base" onClick={() => window.open('/app/settings', '_blank')} data-id="element-2363">
                    <CreditCard className="mr-2 h-5 w-5" data-id="element-2364" />
                    Buy Credits
                  </Button>
                  <Button variant="outline" className="flex-1 h-12 text-base" onClick={() => window.location.reload()} data-id="element-2365">
                    <RefreshCw className="mr-2 h-5 w-5" data-id="element-2366" />
                    Refresh Balance
                  </Button>
                </div>
              </div>
            </Card>
          </div>}

        {/* 5. STATE: RESULTS (Analysis Dashboard) */}
        {!fetching && (alreadyFetched || results) && results && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2367">
            {/* Top Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2368">
              <Card className="p-4 border-l-4 border-l-primary shadow-sm" data-id="element-2369">
                <div className="flex justify-between items-start" data-id="element-2370">
                  <div data-id="element-2371">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider" data-id="element-2372">
                      Total Comments
                    </p>
                    <h3 className="text-2xl font-bold mt-1" data-id="element-2373">
                      {results.total_comments.toLocaleString()}
                    </h3>
                  </div>
                  <div className="p-2 bg-primary/10 rounded-lg text-primary" data-id="element-2374">
                    <MessageSquare size={20} data-id="element-2375" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 border-l-4 border-l-pink-500 shadow-sm" data-id="element-2376">
                <div className="flex justify-between items-start" data-id="element-2377">
                  <div data-id="element-2378">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider" data-id="element-2379">
                      Avg Likes
                    </p>
                    <h3 className="text-2xl font-bold mt-1" data-id="element-2380">
                      {Math.round(results.avg_likes).toLocaleString()}
                    </h3>
                  </div>
                  <div className="p-2 bg-pink-500/10 rounded-lg text-pink-500" data-id="element-2381">
                    <ThumbsUp size={20} data-id="element-2382" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 border-l-4 border-l-info shadow-sm" data-id="element-2383">
                <div className="flex justify-between items-start" data-id="element-2384">
                  <div data-id="element-2385">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider" data-id="element-2386">
                      Engagement Rate
                    </p>
                    <h3 className="text-2xl font-bold mt-1" data-id="element-2387">
                      {results.engagement_rate.toFixed(1)}%
                    </h3>
                  </div>
                  <div className="p-2 bg-info/10 rounded-lg text-info" data-id="element-2388">
                    <TrendingUp size={20} data-id="element-2389" />
                  </div>
                </div>
              </Card>

              <Card className={`p-4 border-l-4 shadow-sm ${results.sentiment.dominant === 'positive' ? 'border-l-success' : results.sentiment.dominant === 'negative' ? 'border-l-destructive' : 'border-l-border'}`} data-id="element-2390">
                <div className="flex justify-between items-start" data-id="element-2391">
                  <div data-id="element-2392">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider" data-id="element-2393">
                      Sentiment
                    </p>
                    <h3 className="text-2xl font-bold mt-1 capitalize" data-id="element-2394">
                      {results.sentiment.dominant}
                    </h3>
                  </div>
                  <div className={`p-2 rounded-lg ${results.sentiment.dominant === 'positive' ? 'bg-success/10 text-success' : results.sentiment.dominant === 'negative' ? 'bg-destructive/10 text-destructive' : 'bg-secondary/50 text-muted-foreground'}`} data-id="element-2395">
                    <SmilePlus size={20} data-id="element-2396" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Sentiment Visualization */}
            <Card className="p-6" data-id="element-2397">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" data-id="element-2398">
                <BarChart3 size={16} data-id="element-2399" />
                Sentiment Distribution
              </h3>
              <div className="space-y-4" data-id="element-2400">
                <div className="flex h-8 rounded-full overflow-hidden w-full text-xs font-bold text-white" data-id="element-2401">
                  <div className="bg-success flex items-center justify-center transition-all duration-1000" style={{
                width: `${results.sentiment.positive}%`
              }} title={`Positive: ${results.sentiment.positive}%`} data-id="element-2402">
                    {results.sentiment.positive > 5 && `${results.sentiment.positive}%`}
                  </div>
                  <div className="bg-muted-foreground flex items-center justify-center transition-all duration-1000" style={{
                width: `${results.sentiment.neutral}%`
              }} title={`Neutral: ${results.sentiment.neutral}%`} data-id="element-2403">
                    {results.sentiment.neutral > 5 && `${results.sentiment.neutral}%`}
                  </div>
                  <div className="bg-destructive flex items-center justify-center transition-all duration-1000" style={{
                width: `${results.sentiment.negative}%`
              }} title={`Negative: ${results.sentiment.negative}%`} data-id="element-2404">
                    {results.sentiment.negative > 5 && `${results.sentiment.negative}%`}
                  </div>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground px-1" data-id="element-2405">
                  <div className="flex items-center gap-1.5" data-id="element-2406">
                    <div className="w-2 h-2 rounded-full bg-success" data-id="element-2407" />
                    <span data-id="element-2408">Positive</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2409">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground" data-id="element-2410" />
                    <span data-id="element-2411">Neutral</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2412">
                    <div className="w-2 h-2 rounded-full bg-destructive" data-id="element-2413" />
                    <span data-id="element-2414">Negative</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Data Grid Section */}
            <Card className="overflow-hidden border-border shadow-md" data-id="element-2415">
              {/* Toolbar */}
              <div className="p-4 border-b border-border bg-secondary/5 flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-2416">
                <div className="relative w-full md:w-80" data-id="element-2417">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-2418" />
                  <Input placeholder="Search comments..." className="pl-9 bg-background" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2419" />
                </div>

                <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0" data-id="element-2420">
                  <div className="flex items-center bg-background border border-border rounded-md px-1 p-1" data-id="element-2421">
                    {(['all', 'positive', 'neutral', 'negative'] as SentimentFilter[]).map(filter => <button key={filter} onClick={() => setSentimentFilter(filter)} className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${sentimentFilter === filter ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-secondary'}`} data-id="element-2422">
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>)}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto" data-id="element-2423">
                <table className="w-full text-sm text-left" data-id="element-2424">
                  <thead className="bg-secondary/20 text-muted-foreground font-medium border-b border-border" data-id="element-2425">
                    <tr data-id="element-2426">
                      <th className="px-4 py-3 w-12 text-center" data-id="element-2427">#</th>
                      <th className="px-4 py-3 w-48" data-id="element-2428">Author</th>
                      <th className="px-4 py-3 min-w-[300px]" data-id="element-2429">Comment</th>
                      <th className="px-4 py-3 text-right cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => handleSort('likes')} data-id="element-2430">
                        <div className="flex items-center justify-end gap-1" data-id="element-2431">
                          Likes {renderSortIcon('likes')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-right cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => handleSort('replies')} data-id="element-2432">
                        <div className="flex items-center justify-end gap-1" data-id="element-2433">
                          Replies {renderSortIcon('replies')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-right cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => handleSort('date')} data-id="element-2434">
                        <div className="flex items-center justify-end gap-1" data-id="element-2435">
                          Date {renderSortIcon('date')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => handleSort('sentiment')} data-id="element-2436">
                        <div className="flex items-center justify-center gap-1" data-id="element-2437">
                          Sentiment {renderSortIcon('sentiment')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50" data-id="element-2438">
                    {paginatedComments.length > 0 ? paginatedComments.map((comment, idx) => {
                  const globalIndex = (currentPage - 1) * itemsPerPage + idx;
                  const isExpanded = expandedComments.has(globalIndex);
                  const text = comment.comment || comment.text || '';
                  const isLong = text.length > 150;
                  const name = getAuthorName(comment);
                  const avatarUrl = getAvatarUrl(comment);
                  return <tr key={globalIndex} className="hover:bg-secondary/5 transition-colors group" data-id="element-2439">
                            <td className="px-4 py-3 text-center text-xs text-muted-foreground" data-id="element-2440">
                              {globalIndex + 1}
                            </td>
                            <td className="px-4 py-3 align-top" data-id="element-2441">
                              <div className="flex items-center gap-2" data-id="element-2442">
                                {avatarUrl ? <img src={avatarUrl} alt={name} className="w-6 h-6 rounded-full object-cover flex-shrink-0" data-id="element-2443" /> : <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-[10px] font-bold text-secondary-foreground" data-id="element-2444">
                                    {name.substring(0, 1).toUpperCase()}
                                  </div>}
                                <div className="font-medium text-foreground text-xs truncate max-w-[140px]" title={name} data-id="element-2445">
                                  {name}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top" data-id="element-2446">
                              <div className={`text-sm leading-relaxed text-foreground/90 ${!isExpanded && isLong ? 'line-clamp-2' : ''}`} data-id="element-2447">
                                {text}
                              </div>
                              {isLong && <button onClick={() => toggleCommentExpand(globalIndex)} className="text-xs text-primary hover:underline mt-1 font-medium" data-id="element-2448">
                                  {isExpanded ? 'Show less' : 'Show more'}
                                </button>}
                            </td>
                            <td className="px-4 py-3 align-top text-right font-mono text-xs" data-id="element-2449">
                              {getLikeCount(comment).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 align-top text-right font-mono text-xs" data-id="element-2450">
                              {getReplyCount(comment).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 align-top text-right text-xs text-muted-foreground whitespace-nowrap" data-id="element-2451">
                              {getTimestamp(comment)}
                            </td>
                            <td className="px-4 py-3 align-top text-center" data-id="element-2452">
                              {renderSentimentBadge(comment.sentiment)}
                            </td>
                          </tr>;
                }) : <tr data-id="element-2453">
                        <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground" data-id="element-2454">
                          <div className="flex flex-col items-center gap-2" data-id="element-2455">
                            <Search size={24} className="opacity-20" data-id="element-2456" />
                            <p data-id="element-2457">No comments found matching your filters.</p>
                          </div>
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              {processedComments.length > 0 && <div className="bg-secondary/5 px-4 py-3 border-t border-border flex justify-between items-center text-xs text-muted-foreground" data-id="element-2458">
                  <div data-id="element-2459">
                    Showing{' '}
                    <span className="font-medium text-foreground" data-id="element-2460">
                      {Math.min(processedComments.length, (currentPage - 1) * itemsPerPage + 1)}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium text-foreground" data-id="element-2461">
                      {Math.min(processedComments.length, currentPage * itemsPerPage)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium text-foreground" data-id="element-2462">
                      {processedComments.length}
                    </span>{' '}
                    results
                  </div>
                  <div className="flex gap-2" data-id="element-2463">
                    <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-2464">
                      Previous
                    </Button>
                    <div className="flex items-center px-2 font-medium" data-id="element-2465">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-id="element-2466">
                      Next
                    </Button>
                  </div>
                </div>}
            </Card>

            <ResearchRoadmap capabilities={researchCapabilities} data-id="element-2467" />
          </div>}
      </div>

      <CreditWarningModal isOpen={showCreditModal} onClose={() => setShowCreditModal(false)} onConfirm={handleConfirmFetch} creditsNeeded={preview?.credits_needed || 0} estimatedTotal={preview?.estimated_total || 0} data-id="element-2468" />

      <PhoneLinkModal isOpen={showPhoneLinkModal} onClose={() => setShowPhoneLinkModal(false)} onConfirm={handlePhoneLinkConfirm} creditsNeeded={preview?.credits_needed || 0} estimatedTotal={preview?.estimated_total || 0} data-id="element-2469" />
    </div>;
}
// Roadmap sub-component
type CapabilityStatus = 'live' | 'coming';
interface Feature {
  name: string;
  live: boolean;
}
interface Capability {
  category: string;
  status: CapabilityStatus;
  icon: React.ElementType;
  features: Feature[];
}
function ResearchRoadmap({
  capabilities
}: {
  capabilities: Capability[];
}) {
  return <div className="mt-2" data-id="element-2470">
      <div className="flex items-center justify-between mb-4" data-id="element-2471">
        <div data-id="element-2472">
          <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-2473">
            <Activity size={16} className="text-primary" data-id="element-2474" />
            Research Capabilities
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2475">
            What's available now and what's on the roadmap
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground" data-id="element-2476">
          <span className="flex items-center gap-1.5" data-id="element-2477">
            <CheckCircle size={12} className="text-success" data-id="element-2478" /> Live
          </span>
          <span className="flex items-center gap-1.5" data-id="element-2479">
            <Lock size={12} className="text-muted-foreground" data-id="element-2480" /> Coming soon
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2481">
        {capabilities.map(cap => {
        const Icon = cap.icon;
        const isLive = cap.status === 'live';
        return <Card key={cap.category} className={`p-4 ${isLive ? 'border-border' : 'border-border/50 opacity-80'}`} data-id="element-2482">
              <div className="flex items-center justify-between mb-3" data-id="element-2483">
                <div className="flex items-center gap-2" data-id="element-2484">
                  <div className={`p-1.5 rounded-md ${isLive ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'}`} data-id="element-2485">
                    <Icon size={14} data-id="element-2486" />
                  </div>
                  <span className="text-sm font-semibold" data-id="element-2487">{cap.category}</span>
                </div>
                {isLive ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20" data-id="element-2488">
                    Live
                  </span> : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border" data-id="element-2489">
                    Soon
                  </span>}
              </div>

              <ul className="space-y-2" data-id="element-2490">
                {cap.features.map(feature => <li key={feature.name} className="flex items-start gap-2" data-id="element-2491">
                    {feature.live ? <CheckCircle size={12} className="text-success mt-0.5 flex-shrink-0" data-id="element-2492" /> : <Circle size={12} className="text-border mt-0.5 flex-shrink-0" data-id="element-2493" />}
                    <span className={`text-xs leading-relaxed ${feature.live ? 'text-foreground/80' : 'text-muted-foreground'}`} data-id="element-2494">
                      {feature.name}
                    </span>
                  </li>)}
              </ul>
            </Card>;
      })}
      </div>
    </div>;
}